import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import Seo from "../../components/feature/Seo";
import ComicCard from "../../components/feature/ComicCard";
import { comics, categories, hotTags, formatViews } from "../../mocks/comics";

const faqs = [
  { q: "策驰漫画网的漫画书库更新频率如何？", a: "我们的漫画书库持续扩充，热门连载作品通常会在更新后数小时内同步入库，同时定期补充编辑精选与完结佳作，让你不错过任何一部好漫画。" },
  { q: "人气排行是如何计算出来的？", a: "人气排行综合了漫画的人气热度、阅读量、收藏数与评分等多个维度，动态更新，方便你快速定位当下最受欢迎的热门漫画。" },
  { q: "编辑精选的漫画有什么特别之处？", a: "编辑精选由资深编辑从画风、剧情、完成度等角度层层筛选，代表了近期值得一看的优质作品，涵盖少年热血、恋爱日常等各类题材。" },
  { q: "如何快速找到自己想看的漫画？", a: "你可以通过漫画库的分类筛选、关键词搜索、热门标签以及人气排行等多种方式，快速缩小范围，定位感兴趣的作品。" },
  { q: "完结推荐的漫画有什么价值？", a: "完结作品可以直接从头看到尾，剧情完整不追更。完结推荐精选了口碑优秀、结局圆满的作品，适合喜欢一口气看完的朋友。" },
];

export default function Home() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [activeCat, setActiveCat] = useState(0);

  const ranking = useMemo(() => [...comics].sort((a, b) => b.views - a.views).slice(0, 10), []);
  const editorPicks = useMemo(() => comics.filter((c) => c.isEditorPick), []);
  const completed = useMemo(() => comics.filter((c) => c.status === "已完结"), []);
  const latest = useMemo(() => [...comics].sort((a, b) => b.chapters - a.chapters).slice(0, 8), []);
  const categoryComics = useMemo(() => comics.filter((c) => c.category === categories[activeCat].name), [activeCat]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (keyword.trim()) navigate(`/fenlei?q=${encodeURIComponent(keyword.trim())}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <Seo
        title="策驰漫画网 - 漫画分类书库 · 人气排行 · 编辑精选完结推荐"
        description="策驰漫画网分类书库齐全，人气排行帮你快速找到热门漫画，编辑精选与完结作品推荐，从少年热血到恋爱日常，书库持续扩充中。"
        canonical="https://artbinge.github.io/"
      />

      {/* ===== 首屏 Hero ===== */}
      <section className="relative w-full h-[540px] md:h-[620px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20anime%20manga%20city%20skyline%20at%20golden%20dusk%2C%20warm%20coral%20orange%20and%20cream%20clouds%2C%20silhouettes%20of%20comic%20characters%20and%20speech%20bubbles%20floating%2C%20artistic%20graphic%20style%2C%20energetic%20youthful%20atmosphere%2C%20high%20detail%20digital%20illustration&width=1600&height=900&seq=hero-main&orientation=landscape"
          alt="策驰漫画网热门漫画书库背景图"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/55 to-foreground-950/80"></div>

        <div className="relative z-10 h-full w-full mx-auto max-w-7xl px-4 md:px-6 flex flex-col items-center justify-center text-center">
          <span className="px-4 py-1.5 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 text-xs font-medium backdrop-blur-sm">
            海量分类书库 · 持续扩充中
          </span>
          <h1 className="mt-5 font-heading text-4xl md:text-6xl text-background-50 leading-tight">
            发现你爱看的<span className="text-accent-400">好漫画</span>
          </h1>
          <p className="mt-4 max-w-2xl text-background-200 text-sm md:text-base leading-relaxed">
            从少年热血到恋爱日常，策驰漫画网分类书库齐全，人气排行帮你找到热门漫画，编辑精选与完结作品推荐一网打尽。
          </p>

          <form onSubmit={handleSearch} className="mt-8 w-full max-w-xl">
            <div className="flex items-center gap-2 bg-background-50/95 rounded-full p-1.5 shadow-soft">
              <i className="ri-search-line pl-4 text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索你感兴趣的漫画..."
                aria-label="搜索漫画"
                className="flex-1 bg-transparent px-2 h-10 text-sm text-foreground-900 placeholder-foreground-400 outline-none"
              />
              <button
                type="submit"
                className="whitespace-nowrap h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
              >
                搜索
              </button>
            </div>
          </form>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/fenlei" className="whitespace-nowrap px-6 py-2.5 rounded-full bg-accent-500 text-foreground-950 text-sm font-bold hover:bg-accent-600 transition-colors">
              浏览漫画库
            </Link>
            <Link to="/paihang" className="whitespace-nowrap px-6 py-2.5 rounded-full bg-background-50/15 border border-background-50/40 text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors">
              查看人气排行
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 分类导航 ===== */}
      <section className="mx-auto max-w-7xl w-full px-4 md:px-6 -mt-10 relative z-20">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 bg-background-50 border border-background-200/70 rounded-2xl p-4 md:p-5 shadow-soft">
          {categories.map((c) => (
            <Link
              key={c.name}
              to={`/fenlei?cat=${encodeURIComponent(c.name)}`}
              className="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-primary-100/60 transition-colors group"
            >
              <span className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center text-lg group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className={c.icon}></i>
              </span>
              <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== 人气排行 ===== */}
      <section className="mx-auto max-w-7xl w-full px-4 md:px-6 py-12 md:py-16" id="paihang">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl md:text-3xl text-primary-700">
              <i className="ri-fire-line text-accent-500"></i>人气排行
            </h2>
            <p className="mt-1 text-sm text-foreground-600">综合人气与评分，帮你找到当下最热门漫画</p>
          </div>
          <Link to="/paihang" className="whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
            查看完整排行 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
          {ranking.map((c, i) => (
            <Link
              key={c.id}
              to={`/manhua/${c.id}`}
              className="flex items-center gap-3 md:gap-4 p-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/40 transition-all group"
            >
              <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center font-heading text-lg ${i < 3 ? "bg-primary-500 text-background-50" : "bg-secondary-200 text-secondary-900"}`}>
                {i + 1}
              </span>
              <div className="w-12 h-16 md:w-14 md:h-20 shrink-0 rounded-md overflow-hidden">
                <img src={c.cover} alt={`${c.title}封面`} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-foreground-950 text-sm md:text-base truncate group-hover:text-primary-600">{c.title}</h3>
                <p className="mt-0.5 text-xs text-foreground-600 truncate">{c.category} · {c.author}</p>
                <p className="mt-0.5 text-xs text-foreground-500 truncate">{c.lastChapter}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="flex items-center justify-end gap-1 text-accent-700 text-sm font-bold">
                  <i className="ri-star-fill text-accent-500"></i>{c.score.toFixed(1)}
                </div>
                <div className="mt-1 text-[11px] text-foreground-500 flex items-center gap-0.5 justify-end">
                  <i className="ri-eye-line"></i>{formatViews(c.views)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== 编辑精选 ===== */}
      <section className="bg-background-100 py-12 md:py-16" id="jingxuan">
        <div className="mx-auto max-w-7xl w-full px-4 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-2xl md:text-3xl text-primary-700">
                <i className="ri-star-smile-fill text-accent-500"></i>编辑精选
              </h2>
              <p className="mt-1 text-sm text-foreground-600">资深编辑层层筛选，优质好漫不容错过</p>
            </div>
            <Link to="/fenlei" className="whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
              更多作品 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {editorPicks.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 分类浏览 ===== */}
      <section className="mx-auto max-w-7xl w-full px-4 md:px-6 py-12 md:py-16" id="fenlei">
        <h2 className="flex items-center gap-2 font-heading text-2xl md:text-3xl text-primary-700 mb-6">
          <i className="ri-grid-fill text-accent-500"></i>分类浏览
        </h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c, i) => (
            <button
              key={c.name}
              type="button"
              onClick={() => setActiveCat(i)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCat === i ? "bg-primary-500 text-background-50" : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-secondary-100"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {categoryComics.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* ===== 完结推荐 ===== */}
      <section className="bg-background-100 py-12 md:py-16" id="wanjie">
        <div className="mx-auto max-w-7xl w-full px-4 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-2xl md:text-3xl text-primary-700">
                <i className="ri-checkbox-circle-fill text-secondary-600"></i>完结推荐
              </h2>
              <p className="mt-1 text-sm text-foreground-600">剧情完整不追更，一次看过瘾的完结佳作</p>
            </div>
            <Link to="/fenlei?status=已完结" className="whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
              全部完结作品 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {completed.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 最新更新 ===== */}
      <section className="mx-auto max-w-7xl w-full px-4 md:px-6 py-12 md:py-16">
        <h2 className="flex items-center gap-2 font-heading text-2xl md:text-3xl text-primary-700 mb-6">
          <i className="ri-loader-4-fill text-accent-500"></i>最新更新
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {latest.map((c) => (
            <Link
              key={c.id}
              to={`/manhua/${c.id}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all group"
            >
              <div className="w-12 h-16 shrink-0 rounded-md overflow-hidden">
                <img src={c.cover} alt={`${c.title}封面`} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-foreground-950 text-sm truncate group-hover:text-primary-600">{c.title}</h3>
                <p className="mt-1 text-xs text-foreground-600 truncate">{c.lastChapter}</p>
                <p className="mt-1 text-[11px] text-accent-700 flex items-center gap-1"><i className="ri-time-line"></i>{c.updateTime}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== 热门标签 ===== */}
      <section className="bg-background-100 py-12 md:py-14">
        <div className="mx-auto max-w-7xl w-full px-4 md:px-6">
          <h2 className="flex items-center gap-2 font-heading text-2xl md:text-3xl text-primary-700 mb-6">
            <i className="ri-price-tag-3-fill text-accent-500"></i>热门标签
          </h2>
          <div className="flex flex-wrap gap-2.5">
            {hotTags.map((t) => (
              <Link
                key={t}
                to={`/fenlei?tag=${encodeURIComponent(t)}`}
                className="px-4 py-2 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 hover:bg-secondary-500 hover:text-background-50 hover:border-secondary-500 transition-colors whitespace-nowrap"
              >
                #{t}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="mx-auto max-w-4xl w-full px-4 md:px-6 py-12 md:py-16" id="faq">
        <h2 className="text-center font-heading text-2xl md:text-3xl text-primary-700 mb-8">常见问题</h2>
        <div className="space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="group bg-background-100 border border-background-200/70 rounded-xl p-4 md:p-5 open:bg-primary-50/40 transition-colors">
              <summary className="flex items-center justify-between gap-3 cursor-pointer font-semibold text-foreground-950 text-sm md:text-base">
                {f.q}
                <i className="ri-add-line text-foreground-500 group-open:rotate-45 transition-transform shrink-0"></i>
              </summary>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="mx-auto max-w-7xl w-full px-4 md:px-6 pb-14 md:pb-18">
        <div className="relative overflow-hidden rounded-2xl bg-primary-600 px-6 py-12 md:py-16 text-center">
          <div className="absolute -top-10 -right-6 w-40 h-40 rounded-full bg-accent-500/30 blur-2xl"></div>
          <div className="absolute -bottom-12 -left-8 w-52 h-52 rounded-full bg-background-50/10 blur-2xl"></div>
          <div className="relative z-10">
            <h2 className="font-heading text-2xl md:text-4xl text-background-50">找不到想看的漫画？</h2>
            <p className="mt-3 text-primary-100 text-sm md:text-base">分类书库持续扩充，热门新作与完结佳作同步更新，帮你快速找到心头好。</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/fenlei" className="whitespace-nowrap px-7 py-3 rounded-full bg-accent-500 text-foreground-950 text-sm font-bold hover:bg-accent-600 transition-colors">
                去漫画库看看
              </Link>
              <Link to="/lianxi" className="whitespace-nowrap px-7 py-3 rounded-full bg-background-50 text-primary-700 text-sm font-semibold hover:bg-primary-100 transition-colors">
                给我们留言
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* FAQPage 结构化数据 */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      }) }} />
    </div>
  );
}